import { API_BASE_URL, VIDA_FITNESS_CENTER_ID } from "@/config/constants";
import {
  getIdToken,
  getAppCheckHeaderToken,
  signOut as firebaseSignOut,
} from "./firebase";

/**
 * A 401 from the backend means Firebase's client-side token is stale/invalid
 * in a way the SDK's own refresh cycle won't recover from (e.g. the session
 * was revoked server-side, or the token simply expired mid-session and the
 * backend rejects it before the SDK's silent refresh kicks in). Firebase's
 * `currentUser` stays non-null in that case, so `ProtectedRoute` never
 * redirects and the user is stuck on a dead page making 401s forever.
 * Forcing a sign-out fires `onAuthStateChanged` with `user = null`, which
 * `ProtectedRoute` picks up to redirect to `/login`. Best-effort: if the
 * sign-out call itself fails, we still throw the original ApiError below.
 */
async function signOutOn401(): Promise<void> {
  try {
    await firebaseSignOut();
  } catch {
    // Best-effort — the ApiError still surfaces to the caller either way.
  }
}

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    message: string,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

interface RequestOptions extends Omit<RequestInit, "body"> {
  body?: unknown;
}

/**
 * API client that auto-injects:
 * - Firebase Bearer token
 * - VIDA_FITNESS_CENTER_ID in path replacements
 */
async function request<T>(
  path: string,
  options: RequestOptions = {},
): Promise<T> {
  const [token, appCheckToken] = await Promise.all([
    getIdToken(),
    getAppCheckHeaderToken(),
  ]);
  const { body, headers: customHeaders, ...rest } = options;

  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    ...(customHeaders as Record<string, string>),
  };

  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  }
  if (appCheckToken) {
    headers["X-Firebase-AppCheck"] = appCheckToken;
  }

  const response = await fetch(resolveUrl(path), {
    ...rest,
    headers,
    body: body ? JSON.stringify(body) : undefined,
  });

  if (!response.ok) {
    if (response.status === 401) await signOutOn401();
    const error = await response
      .json()
      .catch(() => ({ message: response.statusText }));
    throw new ApiError(
      response.status,
      error.message ?? `API Error ${response.status}`,
    );
  }

  // 204 No Content
  if (response.status === 204) return undefined as T;

  const json = await response.json();
  return unwrapPayload<T>(json);
}

/**
 * gundo-admin-fitness-api wraps every response in {success, statusCode, message, payload, type}
 * via responseService.createResponse. Unwrap the payload so callers see the actual data.
 * Non-wrapped responses pass through unchanged.
 */
function unwrapPayload<T>(body: unknown): T {
  if (
    body !== null &&
    typeof body === "object" &&
    "payload" in body &&
    "success" in body
  ) {
    return (body as { payload: T }).payload;
  }
  return body as T;
}

/** Convenience: replace :fitnessCenterId in path with VIDA_FITNESS_CENTER_ID */
function resolvePath(path: string): string {
  return path.replace(":fitnessCenterId", VIDA_FITNESS_CENTER_ID);
}

/**
 * Absolute URLs pass through untouched so feature modules can target a
 * different backend (e.g. the scanner lives in nutritional-engine-api) while
 * reusing the same auth/AppCheck/unwrap plumbing.
 */
function resolveUrl(path: string): string {
  const resolved = resolvePath(path);
  return resolved.startsWith("http") ? resolved : `${API_BASE_URL}${resolved}`;
}

export const api = {
  get: <T>(path: string) => request<T>(resolvePath(path)),

  post: <T>(path: string, body?: unknown) =>
    request<T>(resolvePath(path), { method: "POST", body }),

  put: <T>(path: string, body?: unknown) =>
    request<T>(resolvePath(path), { method: "PUT", body }),

  patch: <T>(path: string, body?: unknown) =>
    request<T>(resolvePath(path), { method: "PATCH", body }),

  delete: <T>(path: string) =>
    request<T>(resolvePath(path), { method: "DELETE" }),

  /** Upload file (multipart). Accepts an optional AbortSignal for cancel/timeout. */
  upload: async <T>(
    path: string,
    formData: FormData,
    options?: { signal?: AbortSignal },
  ): Promise<T> => {
    const [token, appCheckToken] = await Promise.all([
      getIdToken(),
      getAppCheckHeaderToken(),
    ]);
    const headers: Record<string, string> = {};
    if (token) headers["Authorization"] = `Bearer ${token}`;
    if (appCheckToken) headers["X-Firebase-AppCheck"] = appCheckToken;

    const response = await fetch(resolveUrl(path), {
      method: "POST",
      headers,
      body: formData,
      signal: options?.signal,
    });

    if (!response.ok) {
      if (response.status === 401) await signOutOn401();
      const error = await response
        .json()
        .catch(() => ({ message: response.statusText }));
      throw new ApiError(
        response.status,
        error.message ?? `Upload Error ${response.status}`,
      );
    }

    const json = await response.json();
    return unwrapPayload<T>(json);
  },
};
